import { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import ReceivePayments from '../ReceivePayments';
import type { CustomerPayment } from '../../types/customerPayment';
import { ReceivePaymentsHubProvider } from '../ReceivePaymentsHubContext';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';
import {
  parseInventoryHubLocationState,
  resolveReceivePaymentCloseTarget,
} from './inventoryHubNavigation';

const INVOICE_OVERLAY_PATTERN = /\/payment\/receive\/invoice\//;

const InventoryPaymentReceiveOverlay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => parseInventoryHubLocationState(location.state),
    [location.state]
  );
  const invoiceOverlayOpen = INVOICE_OVERLAY_PATTERN.test(location.pathname);

  const close = useCallback(() => {
    const target = resolveReceivePaymentCloseTarget(listSearch, navState);
    navigate(target, { preventScrollReset: true });
  }, [listSearch, navState, navigate]);

  const handlePaymentSaved = useCallback(
    (_payment: CustomerPayment) => {
      refetchList();
      close();
    },
    [close, refetchList]
  );

  return (
    <ReceivePaymentsHubProvider>
      <TransactionOverlay headerless variant="wide" onClose={close}>
        <div
          className={`flex flex-1 min-h-0 flex-col${
            invoiceOverlayOpen ? ' pointer-events-none select-none opacity-60' : ''
          }`}
          aria-hidden={invoiceOverlayOpen}
        >
          <ReceivePayments
            embedded
            initialCustomerId={navState?.customerId}
            initialInvoiceId={navState?.invoiceId}
            onClose={close}
            onPaymentSaved={handlePaymentSaved}
          />
        </div>
      </TransactionOverlay>
      <Outlet />
    </ReceivePaymentsHubProvider>
  );
};

export default InventoryPaymentReceiveOverlay;
